import React, { useCallback, useEffect, useState } from "react";
import { adminPersonalizationApi } from "@/api/personalizationClient";
import ComplianceLock from "@/components/compliance/ComplianceLock";
import { PALETTE } from "@/components/animation/MotionShared";

const STRICTNESS = ["loose", "balanced", "strict"];
const RISK_THRESHOLDS = ["conservative", "moderate", "aggressive"];
const BRIEFING_STYLES = ["concise", "balanced", "deep_dive", "executive"];

function Pill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={!!active}
      style={{
        appearance: "none",
        padding: "5px 11px",
        border: "1px solid",
        borderColor: active ? "#10b981" : PALETTE.goldGlow,
        background: active ? "rgba(16,185,129,0.10)" : "transparent",
        color: active ? "#10b981" : PALETTE.gold,
        borderRadius: 999,
        fontFamily: "'Space Mono', monospace",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.06em",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function Toggle({ value, onChange, label, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      aria-pressed={!!value}
      data-testid={testid}
      style={{
        appearance: "none",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
        padding: "9px 12px",
        border: "1px solid",
        borderColor: value ? "#10b981" : PALETTE.goldGlow,
        background: value ? "rgba(16,185,129,0.10)" : "rgba(11,15,20,0.55)",
        color: value ? "#10b981" : PALETTE.gold,
        borderRadius: 12, cursor: "pointer",
        fontFamily: "'Figtree', system-ui, sans-serif",
        textAlign: "left",
      }}
    >
      <span style={{ fontSize: 13 }}>{label}</span>
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "2px 7px", border: "1px solid currentColor", borderRadius: 999 }}>
        {value ? "ON" : "OFF"}
      </span>
    </button>
  );
}

export default function AdminPersonalizationCommand({ testid = "admin-personalization-command" } = {}) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data: payload } = await adminPersonalizationApi.overview();
      setData(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "overview_failed");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const settings = data?.settings || {};
  const save = async (patch) => {
    setSaving(true); setErr(""); setOk("");
    try {
      const { data: payload } = await adminPersonalizationApi.setSettings(patch);
      setData((d) => ({ ...(d || {}), settings: payload.settings }));
      setOk("Saved.");
      setTimeout(() => setOk(""), 1800);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "save_failed");
    } finally { setSaving(false); }
  };

  const setStrict = (v) => save({ recommendation_strictness: v });
  const setRisk   = (v) => save({ risk_threshold: v });
  const setBrief  = (v) => save({ briefing_format: v });
  const toggleK   = (k) => () => save({ [k]: !settings[k] });
  const setMax    = (v) => save({ max_recommendations: Math.max(1, Math.min(20, Number(v) || 8)) });
  const setW      = (k, v) => save({ [k]: Math.max(0, Math.min(2, Number(v) || 1)) });

  return (
    <section style={S.shell} data-testid={testid}>
      <ComplianceLock variant="banner" policy="v27" testid={`${testid}-compliance-top`} />
      <header style={S.head}>
        <div>
          <span style={S.tagline}>Phase 31 · Personalization Command™</span>
          <h3 style={S.title}>User intelligence overview</h3>
        </div>
        <button type="button" onClick={load} disabled={busy} style={S.refresh} data-testid={`${testid}-refresh`}>{busy ? "…" : "↻"}</button>
      </header>

      {err && <div style={S.err}>⚠ {err}</div>}
      {ok  && <div style={S.ok}>✓ {ok}</div>}

      {/* Quick stats */}
      <div style={S.statsGrid}>
        <Stat label="Users" value={data?.users_total ?? 0} testid={`${testid}-stat-users`} />
        <Stat label="Events tracked" value={data?.events_total ?? 0} testid={`${testid}-stat-events`} />
        <Stat label="Voice usage" value={data?.voice_usage ?? 0} testid={`${testid}-stat-voice`} />
        <Stat label="Image search usage" value={data?.image_search_usage ?? 0} testid={`${testid}-stat-imgsearch`} />
        <Stat label="Briefing opens" value={data?.briefing_engagement ?? 0} testid={`${testid}-stat-briefing`} />
      </div>

      {/* Top assets / markets / regions / archetypes */}
      <div style={S.distGrid}>
        <Block title="Top watched assets" testid={`${testid}-top-assets`}>
          {(data?.top_assets || []).slice(0, 8).map((a) => (
            <Row key={a.symbol} k={a.symbol} v={`${a.count}×`} />
          ))}
          {(!data?.top_assets || data.top_assets.length === 0) && <Empty />}
        </Block>
        <Block title="Top markets" testid={`${testid}-top-markets`}>
          {(data?.top_markets || []).slice(0, 8).map((a) => (
            <Row key={a.market} k={a.market} v={`${a.count}×`} />
          ))}
          {(!data?.top_markets || data.top_markets.length === 0) && <Empty />}
        </Block>
        <Block title="Top regions" testid={`${testid}-top-regions`}>
          {(data?.top_regions || []).slice(0, 8).map((a) => (
            <Row key={a.region} k={a.region} v={`${a.count}×`} />
          ))}
          {(!data?.top_regions || data.top_regions.length === 0) && <Empty />}
        </Block>
        <Block title="Risk distribution" testid={`${testid}-risk-dist`}>
          {Object.entries(data?.risk_distribution || {}).map(([k, v]) => (
            <Row key={k} k={k} v={`${v}×`} />
          ))}
          {Object.keys(data?.risk_distribution || {}).length === 0 && <Empty />}
        </Block>
        <Block title="Archetype distribution" testid={`${testid}-archetype-dist`}>
          {Object.entries(data?.archetype_distribution || {}).filter(([, v]) => Number(v) > 0).map(([k, v]) => (
            <Row key={k} k={k} v={`${v}×`} />
          ))}
          {Object.values(data?.archetype_distribution || {}).every((v) => !v) && <Empty />}
        </Block>
      </div>

      {/* Controls */}
      <div style={S.section}>
        <h4 style={S.sectionTitle}>Master controls</h4>
        <div style={S.grid}>
          <Toggle value={settings.enabled !== false} onChange={(v) => save({ enabled: v })} label="Personalization enabled" testid={`${testid}-toggle-enabled`} />
          <Toggle value={!!settings.sports_enabled} onChange={(v) => save({ sports_enabled: v })} label="Sports recommendations (legality still enforced)" testid={`${testid}-toggle-sports`} />
          <Toggle value={settings.healthcare_public_only !== false} onChange={(v) => save({ healthcare_public_only: v })} label="Healthcare: public information only (MNPI block)" testid={`${testid}-toggle-healthcare`} />
        </div>
      </div>

      <div style={S.section}>
        <h4 style={S.sectionTitle}>Recommendation strictness</h4>
        <div style={S.pillRow}>
          {STRICTNESS.map((s) => (
            <Pill key={s} active={settings.recommendation_strictness === s} onClick={() => setStrict(s)} testid={`${testid}-strict-${s}`}>{s}</Pill>
          ))}
        </div>
      </div>

      <div style={S.section}>
        <h4 style={S.sectionTitle}>Risk threshold</h4>
        <div style={S.pillRow}>
          {RISK_THRESHOLDS.map((s) => (
            <Pill key={s} active={settings.risk_threshold === s} onClick={() => setRisk(s)} testid={`${testid}-risk-${s}`}>{s}</Pill>
          ))}
        </div>
      </div>

      <div style={S.section}>
        <h4 style={S.sectionTitle}>Default briefing format</h4>
        <div style={S.pillRow}>
          {BRIEFING_STYLES.map((s) => (
            <Pill key={s} active={settings.briefing_format === s} onClick={() => setBrief(s)} testid={`${testid}-briefing-${s}`}>{s.replace("_", " ")}</Pill>
          ))}
        </div>
      </div>

      <div style={S.section}>
        <h4 style={S.sectionTitle}>Regional weighting</h4>
        <div style={S.weightRow}>
          {["weight_global", "weight_africa", "weight_local"].map((k) => (
            <label key={k} style={S.weightCol}>
              <span style={S.weightLbl}>{k.replace("weight_", "").toUpperCase()}</span>
              <input
                type="range" min={0} max={2} step={0.1}
                value={settings[k] ?? 1}
                onChange={(e) => setW(k, e.target.value)}
                disabled={saving}
                style={{ accentColor: PALETTE.gold }}
                data-testid={`${testid}-${k}`}
              />
              <span style={S.weightVal}>{Number(settings[k] ?? 1).toFixed(2)}×</span>
            </label>
          ))}
        </div>
      </div>

      <div style={S.section}>
        <h4 style={S.sectionTitle}>Max recommendations</h4>
        <input
          type="number" min={1} max={20} value={settings.max_recommendations || 8}
          onChange={(e) => setMax(e.target.value)}
          disabled={saving}
          style={S.numInput}
          data-testid={`${testid}-max-recommendations`}
        />
      </div>

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance-footer`} />
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div style={S.statCard} data-testid={testid}>
      <span style={S.statLbl}>{label}</span>
      <strong style={S.statVal}>{value}</strong>
    </div>
  );
}
function Block({ title, children, testid }) {
  return (
    <div style={S.block} data-testid={testid}>
      <strong style={S.blockTitle}>{title}</strong>
      <div style={S.blockBody}>{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div style={S.row}>
      <span style={S.rowK}>{String(k || "").toUpperCase()}</span>
      <span style={S.rowV}>{v}</span>
    </div>
  );
}
function Empty() { return <span style={S.empty}>No data yet.</span>; }

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.95), rgba(8,32,22,0.93))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 14, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 12, fontFamily: "'Figtree', system-ui, sans-serif", maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: PALETTE.goldBright, margin: 0, fontSize: 16 },
  refresh: { appearance: "none", background: "transparent", border: `1px solid ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "5px 11px", borderRadius: 8, fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, cursor: "pointer" },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  ok:  { padding: "8px 12px", background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.30)", color: "#d1fae5", borderRadius: 10, fontSize: 12 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 },
  statCard: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 2 },
  statLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  statVal: { color: PALETTE.goldBright, fontSize: 18, fontFamily: "'Space Mono', monospace", fontWeight: 800 },
  distGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 },
  block: { background: "rgba(0,0,0,0.18)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  blockTitle: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.10em" },
  blockBody: { display: "flex", flexDirection: "column", gap: 4 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px", background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 6 },
  rowK: { color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.04em" },
  rowV: { color: PALETTE.gold,       fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 800 },
  empty: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontStyle: "italic" },
  section: { display: "flex", flexDirection: "column", gap: 6 },
  sectionTitle: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.12em", margin: 0 },
  pillRow: { display: "flex", flexWrap: "wrap", gap: 6 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 },
  weightRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 },
  weightCol: { display: "flex", flexDirection: "column", gap: 4 },
  weightLbl: { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  weightVal: { color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  numInput: { width: 80, appearance: "none", background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: PALETTE.slate3, padding: "6px 8px", borderRadius: 8, fontFamily: "'Space Mono', monospace", fontSize: 12 },
};
